import React from 'react';
import PropTypes from 'prop-types';
import { trackEvent } from 'ddc-track-event';
import { setClassNames } from '@ddc/classnames-js';
import { usePrefs, useLabels, useRequestData } from 'wsm-common-data';

import InventoryCount from './InventoryCount';

import { ModelImage } from '../ModelImage';
import { getModelTitle } from '../../utils/getModelTitle';
import { getFormattedPrice } from '../../utils/getFormattedPrice';

const GridCard = ({ modelObject, modelLink, imageOnTop = false }) => {
	const prefs = usePrefs();
	const labels = useLabels();
	const { locale, widgetName, windowId } = useRequestData();

	const { image, model, modelInv, gvBodyStyle } = modelObject;
	const {
		showInventoryOnLotCount,
		modelTitleAttributes,
		startingAtPriceLabel,
		showPricing,
		includeDestinationChargeInPrice,
		isFragmentPage,
		showDivider
	} = prefs;

	const showInventoryCount = showInventoryOnLotCount === 'true';
	// imageOnTop (the stacked look) owns its own padding via the scoped
	// `.model-responsive-container--stacked .model` SCSS rule -- these
	// utility classes are DDC spacing utilities defined with `!important`,
	// so leaving one applied here would always win over that rule
	// regardless of selector specificity (US2007779 T5).
	let modelPaddingClassName = null;
	if (!imageOnTop) {
		modelPaddingClassName = isFragmentPage === 'true' ? 'p-1' : 'p-6';
	}
	const modelClassNames = [
		'model',
		showInventoryCount ? 'showing-count' : null,
		modelPaddingClassName
	];
	const cardClassNames = [
		'model-card',
		'box',
		isFragmentPage !== 'true' ? 'box-border' : 'border-0 bg-transparent',
		'd-flex',
		'flex-column',
		'position-relative',
		imageOnTop && showDivider === 'true' ? 'model-card--divider' : null
	];
	// imageOnTop is exclusively set by the responsive display style's stacked
	// cardLayout (Responsive.jsx) -- no other caller of GridCard ever passes
	// it -- so gating the title size on it, rather than on `isFragmentPage`,
	// tightens the stacked look without touching that pref's unrelated
	// font-weight/spacing side effects on the live grid (US2007779 T1).
	let modelTitleSizeClassName = 'ddc-font-size-large';
	if (imageOnTop) {
		modelTitleSizeClassName = 'ddc-font-size-small';
	} else if (isFragmentPage === 'true') {
		modelTitleSizeClassName = 'font-weight-base';
	}
	const modelTitleClassName = [
		modelTitleSizeClassName,
		'font-weight-bold',
		'justify-content-start',
		'd-flex'
	];
	const priceClassNames = [
		'price',
		'ddc-font-size-small',
		'inline-block',
		'justify-content-start',
		'd-flex',
		isFragmentPage === 'true' ? 'fragment-page-price' : null
	];
	const modelTitle = getModelTitle(modelTitleAttributes, modelObject);
	const formattedPrice = getFormattedPrice(
		locale,
		includeDestinationChargeInPrice === 'true',
		modelObject
	);

	const trackClick = () => {
		trackEvent(widgetName, windowId, {
			action: 'clicked',
			element: 'Model Image',
			elementCTA: modelTitle,
			result: 'Navigated to model listings'
		});
	};

	const titleBlock = (
		<div className=" model-card-grid-title-container d-flex flex-column">
			<span className={setClassNames(modelTitleClassName)}>
				{modelTitle}
			</span>
			{showPricing === 'true' && (
				<span className={setClassNames(priceClassNames)}>
					{`${labels.get(startingAtPriceLabel)} `} {formattedPrice}
				</span>
			)}
		</div>
	);

	// Same reasoning as modelPaddingClassName above: the stacked look's
	// image padding is owned by the scoped SCSS rule, so no py-* utility
	// class should apply when imageOnTop is set (US2007779 T5).
	let imagePaddingClassName = null;
	if (!imageOnTop) {
		imagePaddingClassName = isFragmentPage === 'true' ? 'py-3' : 'py-9';
	}
	const imageContainerClassNames = ['image-container', imagePaddingClassName];

	const imageBlock = (
		<a
			href={modelLink}
			onClick={trackClick}
			className={setClassNames(imageContainerClassNames)}
			data-testid="model-card-grid-component-test"
		>
			<ModelImage url={image} altImage={modelTitle} />
		</a>
	);

	// `imageOnTop` is the stacked look: the current card with the image moved
	// above the title. It's an internal reorder driven by a prop the responsive
	// container sets (NOT a user-facing pref and NOT a new component). It
	// defaults to false so every existing caller — including the live col-* grid
	// — keeps the default title-first order byte-for-byte.
	return (
		<div
			className={setClassNames(cardClassNames)}
			data-testid="model-card-component-test"
		>
			<div className={setClassNames(modelClassNames)}>
				{imageOnTop ? (
					<>
						{imageBlock}
						{titleBlock}
					</>
				) : (
					<>
						{titleBlock}
						{imageBlock}
					</>
				)}
				<InventoryCount
					model={model || modelInv}
					bodyStyle={gvBodyStyle}
				/>
			</div>
		</div>
	);
};

GridCard.propTypes = {
	modelObject: PropTypes.shape({
		year: PropTypes.number,
		make: PropTypes.string,
		model: PropTypes.string,
		modelInv: PropTypes.string,
		gvBodyStyle: PropTypes.string,
		image: PropTypes.string,
		inventoryCount: PropTypes.number,
		minStartingAtPrice: PropTypes.number
	}).isRequired,
	modelLink: PropTypes.string.isRequired,
	imageOnTop: PropTypes.bool
};

export default GridCard;
